import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

export function Share() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['outgoing-shares'],
    queryFn: () => api.getOutgoingShares(),
  });

  const createMutation = useMutation({
    mutationFn: (email: string) => api.createShare(email),
    onSuccess: (response) => {
      if (response.success) {
        setEmail('');
        setError(null);
        setSuccess(`Shared with ${response.share?.recipientEmail}`);
        queryClient.invalidateQueries({ queryKey: ['outgoing-shares'] });
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(response.message || 'Failed to create share');
      }
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (shareId: string) => api.deleteShare(shareId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['outgoing-shares'] });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setError(null);
    setSuccess(null);
    createMutation.mutate(email.trim());
  };

  const shares = data?.shares ?? [];

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <h1 className="text-3xl font-bold text-gray-900">Share Your Journal</h1>
          <p className="mt-1 text-sm text-gray-500">
            Give others read-only access to view your entries
          </p>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Add Share Form */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Add a Share</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <div className="flex gap-3">
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter email address"
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={createMutation.isPending}
                />
                <button
                  type="submit"
                  disabled={!email.trim() || createMutation.isPending}
                  className="px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {createMutation.isPending ? 'Sharing...' : 'Share'}
                </button>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                The user must have an account to receive the share
              </p>
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}

            {success && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
                {success}
              </div>
            )}
          </form>
        </div>

        {/* Existing Shares */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            People with Access
          </h2>

          {isLoading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : shares.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              You haven't shared your journal with anyone yet
            </div>
          ) : (
            <ul className="divide-y divide-gray-200">
              {shares.map((share) => (
                <li key={share.id} className="py-4 flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">
                      {share.recipientName || share.recipientEmail}
                    </p>
                    {share.recipientName && (
                      <p className="text-sm text-gray-500">{share.recipientEmail}</p>
                    )}
                    <p className="text-xs text-gray-400">
                      Shared {new Date(share.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <button
                    onClick={() => deleteMutation.mutate(share.id)}
                    disabled={deleteMutation.isPending}
                    className="px-4 py-2 text-sm text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {deleteMutation.isPending ? 'Removing...' : 'Remove'}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </main>
    </div>
  );
}
